import React, { useState, useCallback } from "react";
import { NickLogo, GoogleS } from "@/components/BrandLogo";
import { Copy, Check, ChevronDown, ChevronUp, Download } from "lucide-react";

const COLORS = [
  { name: "Blu Google", hex: "#4285F4", usage: "Primario, link, CTA deducibile, 1a e 4a banda S" },
  { name: "Rosso Google", hex: "#EA4335", usage: "Urgenza, badge, .com, 2a e 6a banda S, REC" },
  { name: "Giallo Google", hex: "#FBBC04", usage: "Stelle, highlight, Plus package, 3a banda S" },
  { name: "Verde Google", hex: "#34A853", usage: "Successo, conferma, Rooftop, 5a banda S, IN REGALO" },
  { name: "Arancione CTA", hex: "#ff6b35", usage: "Call-to-action principale, bottoni azione" },
  { name: "Sfondo Primario", hex: "#0a0e17", usage: "Background body, dark by design" },
  { name: "Sfondo Card", hex: "#0c1220", usage: "Pannelli, card, popup, modali" },
  { name: "Bianco Testo", hex: "#FFFFFF", usage: "Titoli, testo forte, payoff glow" },
  { name: "Grigio Testo", hex: "#9CA3AF", usage: "Testo secondario, descrizioni" },
  { name: "Grigio Scuro", hex: "#374151", usage: "Bordi, separatori, testo terziario" },
];

const S_BANDS = [
  { color: "#4285F4", name: "Blu", pct: "26%" },
  { color: "#EA4335", name: "Rosso", pct: "12%" },
  { color: "#FBBC04", name: "Giallo", pct: "12%" },
  { color: "#4285F4", name: "Blu", pct: "12%" },
  { color: "#34A853", name: "Verde", pct: "12%" },
  { color: "#EA4335", name: "Rosso", pct: "26%" },
];

const BANNED_WORDS = ["gratis", "sconto", "offerta", "SBP", "SOS", "SOS Business Profile"];

const PACKAGES = [
  { name: "Start", color: "#EA4335", route: "/start", desc: "Pacchetto base assistenza Google Business Profile" },
  { name: "Plus", color: "#FBBC04", route: "/plus", desc: "Assistenza completa + bonus esclusivi", featured: true },
  { name: "Virtual Tour", color: "#0D9488", route: "/deducibile100", desc: "Tour immersivo 499€ (lancio). Timewarp IN REGALO", launch: true },
  { name: "Rooftop", color: "#34A853", route: "/rooftop", desc: "Premium, tutto incluso, priorita' massima" },
];

const MERCH_ITEMS = [
  "T-Shirt Total Black (NickLogo cuore + schedabusiness.com petto + QR schiena)",
  "Penna alluminio nero", "Cover smartphone", "Cover tablet", "Cappello baseball nero",
  "Tazzina caffe ceramica nera", "Tazza tisana/te 350ml", "Bicchiere vetro nero",
  "Accendino metallo nero", "Cavatappi sommelier", "Posacenere ceramica",
  "Bussola tascabile", "Penna USB 32GB", "Block notes A5 + penna",
];

const Section = ({ title, id, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden mb-4" id={id}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 bg-white/[0.03] hover:bg-white/[0.05] transition-colors text-left">
        <h2 className="text-lg font-black text-white">{title}</h2>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 py-5 space-y-4">{children}</div>}
    </div>
  );
};

const ColorSwatch = ({ color }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(color.hex); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={copy} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/20 transition-all w-full text-left group">
      <div className="w-10 h-10 rounded-lg flex-shrink-0 shadow-inner" style={{ background: color.hex }}></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white text-sm font-bold">{color.name}</span>
          <code className="text-gray-500 text-xs">{color.hex}</code>
          {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />}
        </div>
        <span className="text-gray-500 text-xs">{color.usage}</span>
      </div>
    </button>
  );
};

const Code = ({ children }) => (
  <pre className="bg-black/40 border border-white/10 rounded-xl p-4 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap">
    <code>{children}</code>
  </pre>
);

export default function BrandBookPage() {
  return (
    <div className="min-h-screen bg-[#0a0e17] text-gray-300 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <NickLogo size={80} showPayoff={false} />
          <h1 className="text-3xl sm:text-4xl font-black text-white mt-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Brand Book & Documentazione
          </h1>
          <p className="text-sm font-black italic text-white mt-2" style={{ textShadow: '0 0 10px rgba(255,255,255,0.5)' }}>
            se non ti trovano, non esisti
          </p>
          <p className="text-gray-500 text-xs mt-3">
            Guida completa per sviluppatori, grafici, ufficio stampa, tipografia e comunicazione
          </p>
        </div>

        {/* Indice rapido */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8">
          {["Identita'", "Colori", "Logo S", "Logo Download", "Tipografia", "Pacchetti", "Cornici", "Merch", "Tandem .it", "Parole Vietate", "Architettura", "Contatti", "Stampa"].map((item, i) => (
            <a key={i} href={`#${item.toLowerCase().replace(/[^a-z]/g, '')}`} className="text-center text-xs text-gray-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-lg py-2 transition-colors">
              {item}
            </a>
          ))}
        </div>

        {/* IDENTITA' */}
        <Section title="Identita' del Brand" id="identita" defaultOpen={true}>
          <div className="space-y-3 text-sm">
            <p><strong className="text-white">Nome:</strong> schedabusiness.com</p>
            <p><strong className="text-white">Payoff:</strong> <em className="text-white font-black" style={{ textShadow: '0 0 8px rgba(255,255,255,0.4)' }}>"se non ti trovano, non esisti"</em></p>
            <p><strong className="text-white">Fondatore:</strong> Riccardo Rossi — Consulente Business Profile Siena | Fotografo Google Street View Certificato dal 2016</p>
            <p><strong className="text-white">Settore:</strong> Ottimizzazione Google Business Profile, Virtual Tour 360°, Assistenza digitale</p>
            <p><strong className="text-white">Estetica:</strong> Dark by design. Sempre sfondo scuro. Mai sfondi chiari. Il nero fa risaltare i colori Google.</p>
            <p><strong className="text-white">Tono:</strong> Diretto, professionale, mai freddo. Parli a imprenditori e professionisti. Niente tecnicismi inutili.</p>
            <p><strong className="text-white">Place ID Google:</strong> <code className="text-[#4285F4]">ChIJ8Zku0V47KhMRb5aqDGqIbU0</code></p>
            <p><strong className="text-white">Link Condivisione:</strong> <a href="https://share.google/plYYOroJG18abeUnq" target="_blank" rel="noopener noreferrer" className="text-[#4285F4] hover:underline">share.google/plYYOroJG18abeUnq</a></p>
          </div>
        </Section>

        {/* COLORI */}
        <Section title="Palette Colori" id="colori">
          <div className="grid sm:grid-cols-2 gap-2">
            {COLORS.map((c, i) => <ColorSwatch key={i} color={c} />)}
          </div>
          <p className="text-xs text-gray-500 mt-3">Click su un colore per copiare l'HEX. I primi 4 sono i colori ufficiali Google.</p>
        </Section>

        {/* LOGO S */}
        <Section title="Logo S Arcobaleno" id="logos">
          <div className="flex items-center gap-8 mb-4">
            <GoogleS size={120} />
            <div className="space-y-1">
              {S_BANDS.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-6 rounded" style={{ background: b.color, height: i === 0 || i === 5 ? '16px' : '8px' }}></div>
                  <span className="text-xs text-gray-400">{i + 1}. {b.name} — <strong className="text-white">{b.pct}</strong></span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <p><strong className="text-white">Regola:</strong> La 1a (Blu) e la 6a (Rosso) banda sono <strong className="text-white">piu' grandi</strong> (26%). Le 4 centrali sono 12%.</p>
            <p><strong className="text-white">Font S:</strong> Poppins, weight 900, con <code className="text-gray-400">WebkitTextStroke: 1px #000</code> (effetto Uniposca nero)</p>
            <p><strong className="text-white">".com":</strong> Rosso (#EA4335), weight 900, stroke nero, dimensione 28% della S</p>
            <p><strong className="text-white">Varianti:</strong> NickLogo (S.com breve), FullLogo (Schedabusiness.com completo), GoogleS (solo S per divisori)</p>
          </div>
          <Code>{`/* CSS Gradient per la S — 6 bande UGUALI */
linearGradient: {
  Blu    #4285F4  0% → 16.66%
  Rosso  #EA4335  16.66% → 33.33%
  Giallo #FBBC04  33.33% → 50%
  Blu    #4285F4  50% → 66.66%
  Verde  #34A853  66.66% → 83.33%
  Rosso  #EA4335  83.33% → 100%
}`}</Code>

          {/* Download NickLogo ad alta risoluzione */}
          <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl">
            <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
              <Download className="w-4 h-4 text-[#4285F4]" />
              Scarica NickLogo Alta Risoluzione
            </h3>
            <p className="text-gray-400 text-xs mb-3">PNG 1200x1200 su sfondo trasparente. S arcobaleno + .com piccolo.</p>
            <button
              onClick={() => {
                const canvas = document.createElement('canvas');
                canvas.width = 1200;
                canvas.height = 1200;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, 1200, 1200);
                const bands = [
                  { color: '#4285F4', start: 0, end: 0.26 },
                  { color: '#EA4335', start: 0.26, end: 0.38 },
                  { color: '#FBBC04', start: 0.38, end: 0.50 },
                  { color: '#4285F4', start: 0.50, end: 0.62 },
                  { color: '#34A853', start: 0.62, end: 0.74 },
                  { color: '#EA4335', start: 0.74, end: 1.0 },
                ];
                ctx.save();
                ctx.font = '900 800px Poppins, Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 12;
                ctx.strokeText('S', 500, 520);
                ctx.clip(new Path2D());
                ctx.beginPath();
                ctx.rect(0, 0, 1200, 1200);
                ctx.fillStyle = '#000';
                ctx.fillText('S', 500, 520);
                const metrics = ctx.measureText('S');
                const sTop = 520 - metrics.actualBoundingBoxAscent;
                const sHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
                ctx.restore();
                ctx.save();
                ctx.font = '900 800px Poppins, Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 12;
                ctx.strokeText('S', 500, 520);
                bands.forEach(b => {
                  ctx.save();
                  ctx.beginPath();
                  ctx.rect(0, sTop + sHeight * b.start, 1200, sHeight * (b.end - b.start));
                  ctx.clip();
                  ctx.fillStyle = b.color;
                  ctx.fillText('S', 500, 520);
                  ctx.restore();
                });
                ctx.restore();
                ctx.save();
                ctx.font = '900 220px Poppins, Arial, sans-serif';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 8;
                ctx.strokeText('.com', 780, 780);
                ctx.fillStyle = '#EA4335';
                ctx.fillText('.com', 780, 780);
                ctx.restore();
                const link = document.createElement('a');
                link.download = 'NickLogo-S.com-HiRes.png';
                link.href = canvas.toDataURL('image/png');
                link.click();
              }}
              className="bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full px-6 py-2 text-sm font-bold transition-colors"
              data-testid="download-nicklogo-btn"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Scarica PNG 1200x1200
            </button>
          </div>
        </Section>

        {/* LOGO COMPLETO — Download pesante */}
        <Section title="Logo Completo — Download HD" id="logodownload" defaultOpen={true}>
          <div className="space-y-4">
            {/* Logo Preview — Render CSS reale */}
            <div className="bg-[#0a0e17] rounded-2xl border-2 border-[#D4AF37]/30 py-10 px-6 flex flex-col items-center gap-5">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black italic text-center" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 40%, #D4AF37 60%, #B8942E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                schedabusiness.com
              </h2>
              <p className="text-base sm:text-lg font-black italic text-white text-center" style={{ fontFamily: "'Poppins', sans-serif", textShadow: '0 0 12px rgba(255,255,255,0.6), 0 0 24px rgba(255,255,255,0.3)' }}>
                se non ti trovano, non esisti
              </p>
              <div className="flex items-center gap-2">
                <NickLogo size={40} showPayoff={false} />
                <span className="text-2xl font-black italic" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
              </div>
            </div>
            {/* CTA scarica — va alla pagina dedicata */}
            <a
              href="/logo-download"
              className="block w-full text-center bg-[#D4AF37] hover:bg-[#B8942E] text-black rounded-full px-6 py-3 text-sm font-bold transition-colors"
              data-testid="logo-download-page-link"
            >
              <span className="inline-flex items-center gap-2 justify-center">
                <Download className="w-4 h-4" />
                Scarica Logo HD — PNG / JPG
              </span>
            </a>
            <p className="text-gray-600 text-xs text-center">Pagina dedicata con logo oro luxury HD — rendering 2048x1024 per stampa</p>
          </div>
        </Section>

        {/* TIPOGRAFIA */}
        <Section title="Tipografia" id="tipografia">
          <div className="space-y-4">
            <div>
              <p className="text-white font-black text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>Poppins — Font Primario</p>
              <p className="text-xs text-gray-500 mt-1">Google Fonts. Usato per TUTTO: titoli, body, logo, payoff, CTA.</p>
            </div>
            <div className="space-y-2 text-sm">
              <p><strong className="text-white">Titoli H1:</strong> text-4xl / text-5xl, font-black (900), Poppins</p>
              <p><strong className="text-white">Titoli H2:</strong> text-2xl / text-3xl, font-black (900), Poppins</p>
              <p><strong className="text-white">Body:</strong> text-sm / text-base, font-normal, colore gray-300</p>
              <p><strong className="text-white">Payoff:</strong> Poppins, italic, font-black (900), bianco con glow</p>
              <p><strong className="text-white">Info "i":</strong> Georgia, italic, bold — dentro al fumetto nero</p>
            </div>
            <Code>{`/* Payoff text-shadow */
text-shadow: 0 0 10px rgba(255,255,255,0.5),
             0 0 20px rgba(255,255,255,0.2);`}</Code>
          </div>
        </Section>

        {/* PACCHETTI */}
        <Section title="Pacchetti" id="pacchetti">
          <div className="space-y-3">
            {PACKAGES.map((p, i) => (
              <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: p.color }}>
                  <span className={`text-xs font-black ${p.color === '#FFFFFF' || p.color === '#FBBC04' ? 'text-gray-900' : 'text-white'}`}>{i + 1}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">{p.name}</span>
                    <code className="text-gray-600 text-xs">{p.route}</code>
                    {p.featured && <span className="text-[8px] bg-[#FBBC04] text-black font-black px-1.5 py-0.5 rounded-full">BEST SELLER</span>}
                    {p.launch && <span className="text-[8px] bg-white text-black font-black px-1.5 py-0.5 rounded-full">LANCIO</span>}
                  </div>
                  <span className="text-gray-500 text-xs">{p.desc}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3"><strong className="text-white">Deducibile 100%</strong> — TAEG 0%, 3 rate PayPal. Si applica a tutti i pacchetti.</p>
        </Section>

        {/* CORNICI */}
        <Section title="Cornici Arcobaleno (Gradient Border)" id="cornici">
          <p className="text-sm mb-3">Effetto bordo gradient Google. Usare su CTA principali, box statistiche, badge. <strong className="text-white">Max 4-5 per pagina</strong>, non inflazionare.</p>
          <div className="relative overflow-hidden rounded-2xl p-[2px] mb-4">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-60"></div>
            <div className="relative bg-[#0c1220] rounded-[14px] px-5 py-4 text-center">
              <span className="text-white font-bold text-sm">Esempio cornice arcobaleno</span>
            </div>
          </div>
          <Code>{`<div class="relative overflow-hidden rounded-2xl p-[2px]">
  <div class="absolute inset-0 bg-gradient-to-r
    from-[#4285F4] via-[#EA4335]
    via-[#FBBC04] to-[#34A853]
    opacity-60">
  </div>
  <div class="relative bg-[#0c1220]
    rounded-[14px] px-5 py-4">
    <!-- contenuto -->
  </div>
</div>`}</Code>
        </Section>

        {/* MERCH */}
        <Section title="Merchandising Total Black" id="merch">
          <p className="text-sm mb-3">Tutto nero. Logo inciso/stampato. T-shirt <strong className="text-white">IN REGALO</strong> con Plus, Rooftop o Virtual Tour.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {MERCH_ITEMS.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs p-2 bg-white/[0.02] rounded-lg">
                <span className="w-5 h-5 rounded bg-white/10 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0">{i + 1}</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <p><strong className="text-white">T-Shirt Design:</strong></p>
            <p>Fronte: NickLogo S.com + payoff sul cuore (sx), "schedabusiness.com" grande al centro petto, payoff piu' grande sotto</p>
            <p>Retro: QR code al centro della schiena</p>
            <p><strong className="text-white">Personalizzazione:</strong> L'utente puo' caricare il proprio logo/nome che appare sulla maglia (area cuore dx)</p>
          </div>
        </Section>

        {/* TANDEM .IT */}
        <Section title="Tandem schedabusiness.it" id="tandemit">
          <div className="space-y-3 text-sm">
            <p><strong className="text-white">Concetto:</strong> .com e .it sono intrecciate. L'utente naviga tra i due senza sbalzi.</p>
            <p><strong className="text-white">Da .com verso .it:</strong></p>
            <ul className="list-disc list-inside text-gray-400 space-y-1 ml-2">
              <li>CTA "Formazione 100% Detraibile" → schedabusiness.it</li>
              <li>Card intro "Formazione .it" → schedabusiness.it</li>
              <li>Sezione "Deducibile 100%" → schedabusiness.it/#deducibile</li>
            </ul>
            <p><strong className="text-white">Da .it verso .com:</strong></p>
            <ul className="list-disc list-inside text-gray-400 space-y-1 ml-2">
              <li>Pacchetti Start/Plus/Rooftop → schedabusiness.com/start, /plus, /rooftop</li>
              <li>Virtual Tour 360 → schedabusiness.com/deducibile100</li>
              <li>Stessi bottoni flottanti (WhatsApp + Info)</li>
              <li>Stesso Place ID e link condivisione</li>
            </ul>
            <p><strong className="text-white">Allineamento visivo:</strong> Stessa navbar, footer, colori, font, dark mode, cornici arcobaleno, intro splash.</p>
          </div>
        </Section>

        {/* PAROLE VIETATE */}
        <Section title="Parole Vietate" id="parolevietate">
          <div className="flex flex-wrap gap-2 mb-3">
            {BANNED_WORDS.map((w, i) => (
              <span key={i} className="bg-red-500/20 border border-red-500/30 text-red-400 px-3 py-1.5 rounded-lg text-sm font-bold line-through">{w}</span>
            ))}
          </div>
          <div className="space-y-2 text-sm">
            <p><strong className="text-white">Usa invece:</strong></p>
            <p>"IN REGALO" al posto di "gratis/omaggio"</p>
            <p>"schedabusiness.com" al posto di "SBP" o "SOS"</p>
            <p>"Investimento" al posto di "costo/prezzo"</p>
          </div>
        </Section>

        {/* ARCHITETTURA */}
        <Section title="Architettura Tecnica" id="architettura">
          <div className="space-y-3 text-sm">
            <p><strong className="text-white">Frontend:</strong> React.js + Tailwind CSS + Shadcn/UI</p>
            <p><strong className="text-white">Backend:</strong> FastAPI (Python) + MongoDB</p>
            <p><strong className="text-white">Font:</strong> Poppins (Google Fonts)</p>
            <p><strong className="text-white">Icone:</strong> Lucide React (NO emoji per icone UI)</p>
            <p><strong className="text-white">Auth:</strong> JWT + bcrypt (admin /inbox per note vocali)</p>
            <p><strong className="text-white">Audio:</strong> Web Audio API, MediaRecorder (webm/opus)</p>
          </div>
          <Code>{`Struttura file:
/frontend/src/
  components/
    BrandLogo.js      (NickLogo, GoogleS, NavLogo)
    InfoHub.js        (AI Chat + Nota Vocale unificati)
    WhatsAppFloatingMenu.js (Menu sondaggio 11 opzioni)
    IntroSplash.js    (Splash pre-atterraggio)
    DarkModePopup.js  (Tutorial dark mode 30s)
  pages/
    HomePage.js       (Landing principale)
    MerchPage.js      (Merchandising total black)
    BrandBookPage.js  (Questa pagina)
  Layout.js           (Navbar + Footer + Contact)

/backend/
  server.py           (API voice notes, auth, chat)

Rotte API:
  POST /api/voice-note    (Upload nota vocale)
  POST /api/auth/login    (Admin JWT login)
  GET  /api/voice-notes   (Lista note - protetto)
  POST /api/chat          (AI chatbot)`}</Code>
        </Section>

        {/* CONTATTI */}
        <Section title="Contatti e Riferimenti" id="contatti">
          <div className="space-y-2 text-sm">
            <p><strong className="text-white">Riccardo Rossi</strong> — Consulente Business Profile Siena</p>
            <p><strong className="text-white">Telefono:</strong> (+39) 342 038 0578</p>
            <p><strong className="text-white">WhatsApp:</strong> <a href="https://wa.me/+393273839782" className="text-[#4285F4] hover:underline">+39 327 383 9782</a></p>
            <p><strong className="text-white">LinkedIn:</strong> <a href="https://www.linkedin.com/in/riccardorossi1984googletnx/" className="text-[#4285F4] hover:underline" target="_blank" rel="noopener noreferrer">riccardorossi1984googletnx</a></p>
            <p><strong className="text-white">Google Maps:</strong> <a href="https://share.google/plYYOroJG18abeUnq" className="text-[#4285F4] hover:underline" target="_blank" rel="noopener noreferrer">Scheda Business</a></p>
            <p><strong className="text-white">Place ID:</strong> <code className="text-gray-400">ChIJ8Zku0V47KhMRb5aqDGqIbU0</code></p>
          </div>
        </Section>

        {/* STAMPA */}
        <Section title="Per Tipografia e Stampa" id="stampa">
          <div className="space-y-3 text-sm">
            <p><strong className="text-white">Colori CMYK (approssimati):</strong></p>
            <div className="grid sm:grid-cols-2 gap-2">
              <div className="p-2 bg-white/[0.03] rounded-lg"><span className="text-[#4285F4] font-bold">Blu</span> — C:74 M:47 Y:0 K:4</div>
              <div className="p-2 bg-white/[0.03] rounded-lg"><span className="text-[#EA4335] font-bold">Rosso</span> — C:0 M:72 Y:77 K:8</div>
              <div className="p-2 bg-white/[0.03] rounded-lg"><span className="text-[#FBBC04] font-bold">Giallo</span> — C:0 M:25 Y:98 K:2</div>
              <div className="p-2 bg-white/[0.03] rounded-lg"><span className="text-[#34A853] font-bold">Verde</span> — C:70 M:0 Y:60 K:34</div>
            </div>
            <p><strong className="text-white">T-Shirt:</strong> Stampa serigrafia o DTG su cotone nero 100%. Logo a colori, sfondo nero nativo (non stampato).</p>
            <p><strong className="text-white">Oggettistica:</strong> Incisione laser su superfici nere. Logo monocolore bianco dove il colore non e' possibile.</p>
            <p><strong className="text-white">QR Code (retro maglia):</strong> Bianco su nero, link a schedabusiness.com. Dimensione minima 5x5cm.</p>
            <p><strong className="text-white">Biglietti da visita:</strong> Nero totale, logo arcobaleno fronte, QR + contatti retro in bianco.</p>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <NickLogo size={32} showPayoff={true} />
          <p className="text-gray-600 text-xs mt-4">&copy; {new Date().getFullYear()} schedabusiness.com — Brand Book v1.0</p>
        </div>
      </div>
    </div>
  );
}
